// DEBT-VITA™ — Générateur Excel (.xlsx) avec 5 onglets, dont l'onglet
// "Calculs Détaillés" contenant de vraies formules Excel (=B3/B4, etc.)
import * as XLSX from "xlsx";

function safe(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  return v;
}

export function generateDebtExcel({ project, params, results, narrative }) {
  const wb = XLSX.utils.book_new();
  const country = project?.country || "—";
  const year = params?.fiscal_year || project?.fiscal_year || "";
  const currency = params?.currency || "UM";

  // ==========================================================================
  // Onglet 1 — KPIs Vitalistes
  // ==========================================================================
  const sheet1 = [
    ["RAPPORT DEBT-VITA™ — KPIs vitalistes"],
    [`${country} — ${year}`],
    [],
    ["Indicateur", "Valeur", "Unité"],
    ["D1 — Dette par contribuable", safe(results?.dette_par_tete_um), currency],
    ["D2 — Jours de vie hypothéqués", safe(results?.jours_vie_dette), "jours"],
    ["D3 — Années de vie", safe(results?.annees_vie_dette), "ans"],
    ["D4 — % carrière sacrifié", safe(results?.pct_carriere_dette), "%"],
    ["D5 — Service annuel en vies", safe(results?.jours_vie_service_annuel), "jours/an"],
    ["    dont intérêts", safe(results?.jours_vie_interets), "jours"],
    ["    dont capital", safe(results?.jours_vie_capital), "jours"],
    ["D6 — Charge enfant (jours)", safe(results?.jours_vie_enfant), "jours"],
    ["D6 — Charge enfant (années)", safe(results?.annees_vie_enfant), "ans"],
    ["D7 — TAVD", safe(results?.tavd), "%"],
    ["Niveau d'alerte", safe(results?.alert_level?.label), ""],
    ["Vies nationales totales (jours)", safe(results?.dette_vie_nationale_totale), "jours"],
    ["Années de vie collectives", safe(results?.annees_vie_collectives), "ans"],
    ["Jour de libération de la dette", safe(results?.liberation_day_dette), "ème jour de l'année"],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(sheet1);
  ws1["!cols"] = [{ wch: 38 }, { wch: 24 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws1, "1_KPIs_Vitalistes");

  // ==========================================================================
  // Onglet 2 — Calculs Détaillés (FORMULES EXCEL RÉELLES)
  // ==========================================================================
  // Layout :
  //   B2 : daily_salary_used
  //   B3 : active_population
  //   B4 : total_public_debt
  //   B5 : annual_debt_service
  //   B6 : interest_payments
  //   B7 : principal_repayments
  //   B8 : total_prelevements
  //   B9 : active_years (40)
  //
  // Formules :
  //   B12 = B4/B3            (D1)
  //   B13 = B12/B2           (D2)
  //   B14 = B13/365          (D3)
  //   B15 = (B14/B9)*100     (D4)
  //   B16 = (B5/B3)/B2       (D5)
  //   B17 = (B6/B3)/B2       (D5 intérêts)
  //   B18 = (B7/B3)/B2       (D5 capital)
  //   B19 = B8/B2            (JVF)
  //   B20 = (B13/B19)*100    (D7 TAVD)

  const dailySalary = results?.daily_salary_used || 0;
  const activePop = results?.active_population_used || 0;
  const totalDebt = params?.total_public_debt || 0;
  const annualSvc = params?.annual_debt_service || 0;
  const interest = params?.interest_payments || 0;
  const principal = params?.principal_repayments || 0;
  const totalPrel = results?.total_prelevements_used || 0;
  const activeYears = 40;

  const sheet2 = [
    ["DEBT-VITA™ — CALCULS DÉTAILLÉS (formules Excel vivantes)"],
    [],
    ["Variable d'entrée", "Valeur", "Description"],
    ["Salaire journalier (B4)", dailySalary, `${currency}/jour — référent vitaliste`],
    ["Population active (B5)", activePop, "contribuables"],
    ["Dette publique totale (B6)", totalDebt, currency],
    ["Service annuel (B7)", annualSvc, currency],
    ["Intérêts annuels (B8)", interest, currency],
    ["Remboursements capital (B9)", principal, currency],
    ["Total prélèvements/an (B10)", totalPrel, currency],
    ["Durée carrière (B11)", activeYears, "années"],
    [],
    ["Indicateur calculé", "Formule Excel", "Résultat"],
  ];

  // Add row indices to plan formulas: rows are 1-indexed in Excel.
  // The variable rows above produce: B4..B11 (since the headers shift by 1).
  // But XLSX.utils.aoa_to_sheet starts at A1, so:
  //   row 1 = header
  //   row 2 = blank
  //   row 3 = "Variable d'entrée" header
  //   row 4 = Salaire journalier => B4 = dailySalary  ✓
  //   row 5 = Population active  => B5 ✓
  //   row 6 = Dette totale       => B6 ✓
  //   row 7 = Service annuel     => B7 ✓
  //   row 8 = Intérêts           => B8 ✓
  //   row 9 = Capital            => B9 ✓
  //   row 10= Total prélèvements => B10 ✓
  //   row 11= Durée carrière     => B11 ✓
  //   row 12= blank
  //   row 13= Indicateur calculé header
  // Formula rows start at row 14:

  const formulaRows = [
    ["D1 — Dette par contribuable", { f: "B6/B5" }, ""],
    ["D2 — Jours de vie de dette", { f: "B14/B4" }, ""],
    ["D3 — Années de vie", { f: "B15/365" }, ""],
    ["D4 — % carrière sacrifié", { f: "(B16/B11)*100" }, ""],
    ["D5 — Service annuel en jours", { f: "(B7/B5)/B4" }, ""],
    ["D5a — dont intérêts", { f: "(B8/B5)/B4" }, ""],
    ["D5b — dont capital", { f: "(B9/B5)/B4" }, ""],
    ["JVF — Jours vie fiscalité/an", { f: "B10/B4" }, ""],
    ["D7 — TAVD (%)", { f: "(B15/B20)*100" }, ""],
  ];
  formulaRows.forEach((r) => sheet2.push(r));

  const ws2 = XLSX.utils.aoa_to_sheet(sheet2);
  ws2["!cols"] = [{ wch: 32 }, { wch: 22 }, { wch: 30 }];
  // Apply formulas: cells with {f: "..."} are converted to Excel formulas
  // by aoa_to_sheet's interpretation. Confirm by re-walking and setting type.
  Object.keys(ws2).forEach((addr) => {
    if (addr.startsWith("!")) return;
    const cell = ws2[addr];
    if (cell && typeof cell.f === "string") {
      cell.t = "n";
      delete cell.v; // let Excel compute on open
    }
  });
  XLSX.utils.book_append_sheet(wb, ws2, "2_Calculs_Detailles");

  // ==========================================================================
  // Onglet 3 — Projection N années
  // ==========================================================================
  const proj = results?.projection_data || [];
  const sheet3 = [["Année", "Dette totale", "Pop. active", "Dette/tête", "Jours vie", "Années vie"]];
  proj.forEach((p) => {
    sheet3.push([
      p.annee,
      safe(p.dette_totale),
      safe(p.pop_active),
      safe(p.dette_par_tete),
      safe(p.jours_vie),
      safe(p.annees_vie),
    ]);
  });
  const ws3 = XLSX.utils.aoa_to_sheet(sheet3);
  ws3["!cols"] = [{ wch: 8 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws3, "3_Projection");

  // ==========================================================================
  // Onglet 4 — Créanciers
  // ==========================================================================
  const breakdown = results?.creditor_breakdown || [];
  const sheet4 = [
    ["Créancier", "Type", "Montant", "Part (%)", "Dette/tête", "Jours vie/tête", "Années vie/tête", "Coût intérêts/jour"],
  ];
  breakdown.forEach((c) => {
    sheet4.push([
      safe(c.name),
      safe(c.type),
      safe(c.amount),
      safe(c.part_pct),
      safe(c.dette_par_tete),
      safe(c.jours_vie),
      safe(c.annees_vie),
      safe(c.cost_of_interest_per_capita_daily),
    ]);
  });
  const ws4 = XLSX.utils.aoa_to_sheet(sheet4);
  ws4["!cols"] = [
    { wch: 24 }, { wch: 14 }, { wch: 16 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
  ];
  XLSX.utils.book_append_sheet(wb, ws4, "4_Creanciers");

  // ==========================================================================
  // Onglet 5 — Données brutes
  // ==========================================================================
  const sheet5 = [
    ["Paramètre", "Valeur"],
    ["Pays", country],
    ["Année fiscale", year],
    ["Devise", currency],
    ["Dette publique totale", safe(params?.total_public_debt)],
    ["Dette intérieure", safe(params?.domestic_debt)],
    ["Dette extérieure", safe(params?.external_debt)],
    ["Ratio dette/PIB (%)", safe(params?.debt_to_gdp_ratio)],
    ["Service annuel", safe(params?.annual_debt_service)],
    ["Intérêts annuels", safe(params?.interest_payments)],
    ["Remboursements capital", safe(params?.principal_repayments)],
    ["Taux d'intérêt moyen (%)", safe(params?.average_interest_rate)],
    ["Maturité moyenne (ans)", safe(params?.average_maturity_years)],
    ["Déficit fiscal annuel", safe(params?.annual_fiscal_deficit)],
    ["PIB total", safe(params?.gdp_total)],
    ["Population totale", safe(params?.total_population)],
    ["Population active", safe(params?.active_population)],
    ["Population < 18 ans", safe(params?.youth_population)],
    ["Horizon projection (ans)", safe(params?.projection_years)],
    ["Croissance PIB (%/an)", safe(params?.gdp_growth_rate)],
    ["Croissance pop. (%/an)", safe(params?.population_growth_rate)],
    ["Tendance déficit (%/an)", safe(params?.expected_deficit_trend)],
    ["Source des données", safe(params?.data_source)],
    ["Date de génération", new Date().toLocaleString("fr-FR")],
  ];
  const ws5 = XLSX.utils.aoa_to_sheet(sheet5);
  ws5["!cols"] = [{ wch: 30 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, ws5, "5_Donnees_Brutes");

  // ==========================================================================
  // Save
  // ==========================================================================
  const today = new Date().toISOString().slice(0, 10);
  const countrySlug = (country && country !== "—" ? country : "Pays_non_specifie").replace(/\s+/g, "_");
  const filename = `VITAE-PUBLICA_DEBT-VITA_${countrySlug}_${year || "----"}_${today}.xlsx`;
  XLSX.writeFile(wb, filename, { bookType: "xlsx" });
  return filename;
}
